import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { PeriodTotals } from './types';

/**
 * The chrome the three tabs share: a date range, the period totals line, and
 * the loading / error / empty states.
 *
 * Writing it once is what stops the three from drifting into three slightly
 * different ideas of the same list, which is exactly what happened to the five
 * legacy files this replaces — each had its own date picker, its own spinner
 * and its own wording for "nothing found".
 */

/**
 * Purchased and redeemed for the window.
 *
 * Only the ledger tabs use this. Their endpoint sums the same query its rows
 * come from, so the figures cover the whole period rather than the page — which
 * is what the wording says. The card search returns the same two fields and
 * computes them differently; see `PeriodTotals` in ./types.
 */
export function PeriodSummary({
  totals,
  shown,
  total,
}: {
  totals: PeriodTotals | undefined;
  shown: number;
  total: number;
}) {
  const { t } = useTranslation();
  if (!totals) return null;

  return (
    <div className="text-muted-foreground space-y-1 text-sm">
      <p aria-live="polite">
        {t('giftCards.showingOf', { shown, total })}
      </p>
      <p className="text-xs">
        {t('giftCards.periodTotals', {
          purchased: formatCurrency(totals.purchased),
          redeemed: formatCurrency(totals.redeemed),
        })}
      </p>
    </div>
  );
}

export function TabStates({
  isError,
  isPending,
  isEmpty,
  busy,
  emptyMessage,
  onRetry,
  children,
}: {
  isError: boolean;
  isPending: boolean;
  isEmpty: boolean;
  /** A refetch in flight — shown over the empty state, which has no rows. */
  busy?: boolean;
  emptyMessage: string;
  onRetry: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-3">
      {isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('giftCards.loadFailed')}</p>
            <Button variant="outline" size="sm" onClick={onRetry}>
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-3">
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-52" />
              </div>
              <Skeleton className="h-5 w-20" />
            </div>
          ))}
        </div>
      )}

      {isEmpty && (
        <div className="relative rounded-xl">
          <BusyOverlay show={busy} />
          <p
            className={cn(
              'text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm',
              busy && 'opacity-60 transition-opacity'
            )}
          >
            {emptyMessage}
          </p>
        </div>
      )}

      {children}
    </div>
  );
}
